import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThemeToggle from "./ToggleSwitch";
import CreateSessionButton from "./button/CreateSessionButton";
import { ROUTES } from "../routes";
import { useTheme } from "../features/theme/useTheme";

const LeftMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { themeMode, changeTheme } = useTheme();

  // Helper function to check if a route is active
  const isActiveRoute = (route: string) => {
    if (route === ROUTES.HOME_PAGE) {
      // For home page, match exactly or root path
      return (
        location.pathname === ROUTES.HOME_PAGE ||
        location.pathname === ROUTES.HOME
      );
    }
    if (route === ROUTES.SESSIONS) {
      // For sessions, match sessions path and its sub-routes
      return location.pathname.startsWith("/sessions");
    }
    if (route === ROUTES.MEMBERS) {
      // For members, match members path and its sub-routes
      return location.pathname.startsWith("/members");
    }
    return location.pathname === route;
  };
  return (
    <div className="sticky top-20 left-0 hidden h-full shrink-0 lg:block">
      {/* Menu */}
      <div className="w-full min-w-[144px] shrink-0 px-10 py-2">
        <nav className="w-full flex-1 shrink-0" role="menu">
          <ul className="w-full space-y-4">
            <li>
              <a
                href="#"
                className={`menu-link group/link ${
                  isActiveRoute(ROUTES.HOME_PAGE) ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTES.HOME_PAGE);
                }}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="fill-grey-50"
                    d="M11.3387 1.24963C11.7161 0.91842 12.2807 0.918413 12.658 1.24963L19.1483 6.99963L19.9979 7.73987L21.658 9.24963C22.0717 9.61439 22.1119 10.2454 21.7479 10.6598C21.3831 11.0734 20.7531 11.1136 20.3387 10.7496L19.9979 10.4498V18.9996C19.9979 20.6565 18.6547 21.9996 16.9979 21.9996H13.9979C13.4458 21.9994 12.9979 21.5518 12.9979 20.9996V15.9996C13.002 15.7394 12.8976 15.4893 12.7088 15.3102C12.5234 15.1156 12.2666 15.0034 11.9979 14.9996C11.4459 14.9999 10.9981 15.4477 10.9979 15.9996V20.9996C10.9979 21.5519 10.5502 21.9996 9.99788 21.9996H6.99788C5.34125 21.9994 3.99788 20.6563 3.99788 18.9996V10.4498L3.65804 10.7496C3.24112 11.0854 2.63376 11.0333 2.28011 10.6315C1.92676 10.2296 1.95272 9.62037 2.3387 9.24963L3.99788 7.73987L4.84847 6.99963L11.3387 1.24963ZM5.99788 8.66956V18.9996C5.99788 19.5518 6.44582 19.9994 6.99788 19.9996H8.99788V15.9996C8.99808 14.3431 10.3414 12.9999 11.9979 12.9996C13.6546 12.9996 14.9977 14.3429 14.9979 15.9996V19.9996H16.9979C17.5502 19.9996 17.9979 19.5519 17.9979 18.9996V8.66956L11.9979 3.34045L5.99788 8.66956Z"
                  />
                </svg>
                {t("navigation.home")}
              </a>
            </li>
            <li>
              <a
                href="#"
                className={`menu-link ${
                  isActiveRoute(ROUTES.SESSIONS) ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTES.SESSIONS);
                }}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 2C7.55228 2 8 2.44772 8 3V4H16V3C16 2.44772 16.4477 2 17 2C17.5523 2 18 2.44772 18 3V4H19C20.6569 4 22 5.34315 22 7V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V7C2 5.34315 3.34315 4 5 4H6V3C6 2.44772 6.44772 2 7 2ZM19 10H5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10ZM16 12C16.5523 12 17 12.4477 17 13V15C17 15.5523 16.5523 16 16 16H14C13.4477 16 13 15.5523 13 15V13C13 12.4477 13.4477 12 14 12H16ZM16 6H6C5.44772 6 5 6.44772 5 7V8H19V7C19 6.44772 18.5523 6 18 6H16Z"
                    fill="#808080"
                  />
                </svg>
                {t("navigation.sessions")}
              </a>
            </li>
            <li>
              <a
                href="#"
                className={`menu-link ${
                  isActiveRoute(ROUTES.MEMBERS) ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTES.MEMBERS);
                }}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6699 13.13C18.0399 14.06 18.9999 15.32 18.9999 17V20H22.9999V17C22.9999 14.82 19.4299 13.53 16.6699 13.13Z"
                    fill="#808080"
                  />
                  <path
                    d="M14.9999 12C17.2099 12 18.9999 10.21 18.9999 8C18.9999 5.79 17.2099 4 14.9999 4C14.5299 4 14.0899 4.1 13.6699 4.24C14.4999 5.27 14.9999 6.58 14.9999 8C14.9999 9.42 14.4999 10.73 13.6699 11.76C14.0899 11.9 14.5299 12 14.9999 12Z"
                    fill="#808080"
                  />
                  <path
                    d="M8.99994 12C11.2099 12 12.9999 10.21 12.9999 8C12.9999 5.79 11.2099 4 8.99994 4C6.78994 4 4.99994 5.79 4.99994 8C4.99994 10.21 6.78994 12 8.99994 12ZM8.99994 6C10.0999 6 10.9999 6.9 10.9999 8C10.9999 9.1 10.0999 10 8.99994 10C7.89994 10 6.99994 9.1 6.99994 8C6.99994 6.9 7.89994 6 8.99994 6Z"
                    fill="#808080"
                  />
                  <path
                    d="M8.99994 13C6.32994 13 0.999939 14.34 0.999939 17V20H16.9999V17C16.9999 14.34 11.6699 13 8.99994 13ZM14.9999 18H2.99994V17.01C3.19994 16.29 6.29994 15 8.99994 15C11.6999 15 14.7999 16.29 14.9999 17V18Z"
                    fill="#808080"
                  />
                </svg>
                {t("navigation.members")}
              </a>
            </li>
          </ul>
        </nav>
        <hr className="border-grey-70 my-2" />

        {/* Theme Toggle Section */}
        <ThemeToggle mode={themeMode} onChange={changeTheme} />

        <div className="mt-6 flex justify-center">
          <CreateSessionButton text={t("navigation.newSession")} />
        </div>
      </div>
    </div>
  );
};

export default LeftMenu;
